import { Box, Group, Loader, Stack } from '@mantine/core';
import { FormEventHandler, useEffect, useState } from 'react';
import FormFooter from 'components/forms/FormFooter';

/**
 * Parameters of the FormWrapper component.
 */
export interface FormWrapperProps {
  primaryButtonText: string;
  primaryIcon: React.ReactNode;
  secondaryButtonText?: string;
  secondaryIcon?: React.ReactNode;
  closeConfirmTitle?: string;
  closeConfirmMessage?: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
  loading: boolean;
  /** Hides the complete form only until its input definitions first finish loading. */
  contentLoading?: boolean;
  children: React.ReactNode;
  pt?: number;
  hidePrimaryButton?: boolean;
  hideSecondaryButton?: boolean;
  skipSecondaryButtonConfirm?: boolean;
  hideFooter?: boolean;
  footerRightSide?: React.ReactNode;
  onSecondaryClick?: () => void;
}

/**
 * Wrapper for forms.
 */
export default function FormWrapper({
  primaryButtonText,
  primaryIcon,
  secondaryButtonText,
  secondaryIcon,
  closeConfirmTitle,
  closeConfirmMessage,
  onSubmit,
  loading,
  contentLoading = false,
  children,
  pt,
  hidePrimaryButton = false,
  hideSecondaryButton = false,
  skipSecondaryButtonConfirm = false,
  hideFooter = false,
  footerRightSide,
  onSecondaryClick,
}: FormWrapperProps) {
  const [contentLoaded, setContentLoaded] = useState(!contentLoading);
  useEffect(() => {
    if (!contentLoading) setContentLoaded(true);
  }, [contentLoading]);
  const initialLoading = contentLoading && !contentLoaded;

  return (
    <Box
      pt={pt}
      sx={(theme) => ({ borderRadius: theme.radius.sm, backgroundColor: initialLoading ? 'transparent' : theme.white })}
    >
      {initialLoading && (
        <Group position="center" py={80} role="status" aria-label="Načítavanie formulára">
          <Loader />
        </Group>
      )}
      <form onSubmit={onSubmit} style={initialLoading ? { display: 'none' } : undefined} aria-busy={contentLoading}>
        <Stack spacing={0} pb={16}>
          {children}
        </Stack>

        {!hideFooter && (
          <FormFooter
            loading={loading}
            primaryButtonText={primaryButtonText}
            primaryIcon={primaryIcon}
            secondaryButtonText={secondaryButtonText}
            secondaryIcon={secondaryIcon}
            hidePrimaryButton={hidePrimaryButton}
            hideSecondaryButton={hideSecondaryButton}
            skipSecondaryButtonConfirm={skipSecondaryButtonConfirm}
            closeConfirmTitle={closeConfirmTitle}
            closeConfirmMessage={closeConfirmMessage}
            rightSide={footerRightSide}
            onSecondaryClick={onSecondaryClick}
          />
        )}
      </form>
    </Box>
  );
}
